import React, { useEffect, useRef } from 'react';

import clsx from 'clsx';
import { PanelTopHeader } from '@/components/panels/PanelTopHeader/PanelTopHeader';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Layers as CraftLayers, useLayer } from '@craftjs/layers';
import { ROOT_NODE } from '@craftjs/utils';
import IconSection from '@/assets/icons/icon-section.svg?react';
import IconImage from '@/assets/icons/icon-image.svg?react';
import IconText from '@/assets/icons/icon-text.svg?react';
import IconPlay from '@/assets/icons/icon-play.svg?react';
import IconMoreHorizontalSquare from '@/assets/icons/icon-more-horizontal-square.svg?react';
import IconMinusSignSquare from '@/assets/icons/icon-minus-sign-square.svg?react';
import IconFilterMailSquare from '@/assets/icons/icon-filter-mail-square.svg?react';
import IconMail from '@/assets/icons/icon-mail.svg?react';
import IconTextSquare from '@/assets/icons/icon-text-square.svg?react';
import IconCall from '@/assets/icons/icon-call.svg?react';
import IconCheckmarkSquare from '@/assets/icons/icon-checkmark-square.svg?react';
import IconLeftToRightListBullet from '@/assets/icons/icon-left-to-right-list-bullet.svg?react';
import IconCheckList from '@/assets/icons/icon-check-list.svg?react';
import { Layer } from '@/components/layers/Layer/Layer';
import {
  BUTTON_NODE_NAME,
  CONTENT_BLOCK_NAME,
  IMAGE_NODE_NAME,
  FORM_NODE_NAME,
  FORM_TEXT_FIELD_NODE_NAME,
  FORM_EMAIL_FIELD_NODE_NAME,
  FORM_PHONE_FIELD_NODE_NAME,
  FORM_CONSENT_FIELD_NODE_NAME,
  FORM_DROPDOWN_FIELD_NODE_NAME,
  FORM_SELECTION_FIELD_NODE_NAME,
  FORM_SUBMIT_BUTTON_NODE_NAME,
  PAGE_NODE_NAME,
  TEXT_NODE_NAME,
  VIDEO_NODE_NAME,
} from '@/utils/constants';
import { useEditor } from '@craftjs/core';
import { LayerCollapse } from '@/components/layers/LayerCollapse/LayerCollapse';
import { isContainerNode, isFormNode, isPopupNode } from '@/utils/helperFunc';
import { useScrollNodeIntoView } from '@/hooks/useScrollNodeIntoView';

interface LayersProps {
  className: string;
  onLayersClick?: (elementName: string) => void;
}

export const Layers: React.FC<LayersProps> = ({ className, onLayersClick }) => {
  const handleClick = (elementName: string) => {
    if (onLayersClick) {
      onLayersClick(elementName);
    }
  };

  const layerIcons: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    [CONTENT_BLOCK_NAME]: IconSection,
    [IMAGE_NODE_NAME]: IconImage,
    [TEXT_NODE_NAME]: IconText,
    [VIDEO_NODE_NAME]: IconPlay,
    [BUTTON_NODE_NAME]: IconMoreHorizontalSquare,
    [FORM_NODE_NAME]: IconFilterMailSquare,
    [FORM_TEXT_FIELD_NODE_NAME]: IconTextSquare,
    [FORM_EMAIL_FIELD_NODE_NAME]: IconMail,
    [FORM_PHONE_FIELD_NODE_NAME]: IconCall,
    [FORM_CONSENT_FIELD_NODE_NAME]: IconCheckmarkSquare,
    [FORM_DROPDOWN_FIELD_NODE_NAME]: IconLeftToRightListBullet,
    [FORM_SELECTION_FIELD_NODE_NAME]: IconCheckList,
    [PAGE_NODE_NAME]: IconMinusSignSquare,
  };

  const layerRowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const layerContainer = layerRowRef.current;
    if (!layerContainer) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[draggable="true"]')) {
        window.__lastDragSource = 'sidebar';
      }
    };

    layerContainer.addEventListener('mousedown', handleMouseDown);

    return () => {
      layerContainer.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  const renderLayer = ({ children }: { children: React.ReactNode }) => {
    const {
      id,
      connectors,
      actions,
      depth,
      expanded,
      children: childNodes,
    } = useLayer((layer) => {
      return {
        expanded: layer.expanded,
      };
    });

    const scrollNodeIntoView = useScrollNodeIntoView();

    const {
      nodeTitle,
      parentOrSiblingIsActive,
      nodeName,
      isActive,
      nodeIsContainer,
      isPopup,
      descendantOfActive,
      directParentActive,
      suppressChildRounded,
      siblingOfActiveLeaf,
      actions: { selectNode },
    } = useEditor((_, query) => {
      let parentOrSiblingIsActive = false;
      let isChildSelected = false;
      let descendantOfActive = false;
      let directParentActive = false;
      let suppressChildRounded = false;
      let siblingOfActiveLeaf = false;

      const activeNodeId = query.getEvent('selected').first();
      if (activeNodeId) {
        const activeNode = query.node(activeNodeId).get();
        const currentNode = query.node(id).get();

        const activeHasChildren = (activeNode?.data?.nodes?.length ?? 0) > 0;
        const activeIsRootOrPage =
          activeNode?.id === ROOT_NODE || activeNode?.data?.displayName === 'Page';
        const activeIsContainerLike =
          isContainerNode(activeNode) ||
          isFormNode(activeNode) ||
          activeHasChildren ||
          activeIsRootOrPage;

        if (activeIsContainerLike) {
          parentOrSiblingIsActive = currentNode.data.parent === activeNode.id;
          // Highlight every descendant of the active container-like node
          let cursorId: string | null = currentNode.id as string;
          while (cursorId) {
            const cursor = query.node(cursorId).get();
            const parentId = cursor?.data.parent as string | undefined;
            if (!parentId) break;
            if (parentId === activeNode.id) {
              descendantOfActive = true;
              break;
            }
            if (parentId === cursorId) break; // safety
            cursorId = parentId;
          }
        } else {
          parentOrSiblingIsActive = false;
          const activeParentId = activeNode?.data?.parent as string | undefined;
          if (activeParentId && currentNode?.id === activeParentId) {
            directParentActive = true;
          }
          // If THIS current node is the active leaf, suppress its rounding since
          // the parent collapse will be active and own the rounding.
          if (activeNode?.id === currentNode?.id) {
            suppressChildRounded = true;
          }
          // Gray-highlight siblings under the same active parent (excluding the active node)
          if (
            activeParentId &&
            currentNode?.data?.parent === activeParentId &&
            currentNode?.id !== activeNode?.id
          ) {
            siblingOfActiveLeaf = true;
          }
        }

        if (!activeIsContainerLike && activeNode.id !== id) {
          let parentCursor = activeNode.data.parent as string | null | undefined;
          while (parentCursor) {
            if (parentCursor === id) {
              isChildSelected = true;
              break;
            }
            const parentNode = query.node(parentCursor).get();
            if (!parentNode) break;
            if (parentNode.data.parent === parentCursor) break; // safety against self-references
            parentCursor = parentNode.data.parent as string | null | undefined;
          }
        }

        if (isChildSelected) {
          descendantOfActive = false;
        }
      }

      return {
        nodeTitle: query.node(id).get().data.custom.displayName,
        nodeName: query.node(id).get().data.displayName,
        isActive: query.getEvent('selected').first() === id,
        parentOrSiblingIsActive,
        isPopup: isPopupNode(query.node(id).get()),
        descendantOfActive,
        directParentActive,
        suppressChildRounded,
        siblingOfActiveLeaf,
        childNodeIds: query.node(id).get().data.nodes.length,
        nodeIsContainer: isContainerNode(query.node(id).get()),
      };
    });

    // Hide Form Submit Button from Layers tree entirely
    if (nodeName === FORM_SUBMIT_BUTTON_NODE_NAME) {
      return null;
    }

    const layerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      actions.setExpandedState(true);
    }, [id]);

    // When this layer becomes the selected one (e.g. user selected on canvas), scroll it into view
    useEffect(() => {
      if (isActive && layerRef.current) {
        layerRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, [isActive]);

    let type: 'selected' | 'default' | 'empty' = 'empty';
    if (isActive) {
      type = 'selected';
    } else if (parentOrSiblingIsActive || descendantOfActive) {
      type = 'default';
    } else {
      type = 'empty';
    }

    const shouldRenderCollapse =
      childNodes.length > 0 || nodeName === PAGE_NODE_NAME || nodeIsContainer;

    return (
      <div
        ref={(ref) => {
          layerRef.current = ref;
          if (ref) connectors.layer(ref as HTMLElement);
        }}
      >
        {shouldRenderCollapse ? (
          <LayerCollapse
            depthLevel={childNodes.length > 0 ? depth : depth - 1}
            className=""
            text={nodeTitle}
            icon={layerIcons[nodeName]}
            isOpen={expanded}
            isActive={Boolean(isActive || directParentActive)}
            isHighlighted={!isActive && descendantOfActive}
            hasChildren={childNodes.length > 0}
            onSelect={() => {
              selectNode(id);
              if (isPopup) scrollNodeIntoView(id, true);
            }}
            onToggleCollapse={() => {
              actions.toggleLayer();
            }}
            childrenContent={children}
          />
        ) : (
          <Layer
            depthLevel={depth - 1}
            className=""
            icon={layerIcons[nodeName]}
            isActive={isActive}
            isHighlighted={!isActive && descendantOfActive}
            text={nodeTitle}
            type={type}
            suppressRounded={suppressChildRounded}
            mutedSibling={siblingOfActiveLeaf}
            onClick={() => {
              selectNode(id);
              if (isPopup) scrollNodeIntoView(id, true);
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div className={clsx(className, 'flex h-full flex-col')}>
      <PanelTopHeader
        text="Layers"
        backButton={false}
        onClickClose={() => handleClick('')}
        className={''}
        saveBtnShow={false}
      />

      <ScrollArea className="flex w-full flex-1 overflow-hidden p-6">
        <div ref={layerRowRef} className="craft-layers-container relative max-w-[232px]">
          <CraftLayers expandRootOnLoad={true} renderLayer={renderLayer} />
        </div>
      </ScrollArea>
    </div>
  );
};
